import { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'

function timeAgo(ts) {
  if (!ts) return ''
  const diff = Date.now() - new Date(ts).getTime()
  const m = Math.floor(diff / 60000)
  if (diff < 60000) return 'now'
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h`
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function initials(name) {
  return (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
}


export default function FloatingChat() {
  const [open, setOpen] = useState(false)
  const [threads, setThreads] = useState([])        // { client_id, name, lastMsg, unread }
  const [selectedId, setSelectedId] = useState(null)
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [totalUnread, setTotalUnread] = useState(0)
  const bottomRef = useRef(null)
  const inputRef = useRef(null)
  const channelRef = useRef(null)

  // Load thread list
  useEffect(() => {
    loadThreads()
  }, [])

  // Realtime: new inbound messages
  useEffect(() => {
    channelRef.current = supabase
      .channel('floating-chat-inbound')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, payload => {
        const msg = payload.new
        if (msg.sender === 'client') {
          setTotalUnread(n => n + 1)
          setThreads(prev => {
            const existing = prev.find(t => t.client_id === msg.client_id)
            if (existing) {
              return prev.map(t => t.client_id === msg.client_id
                ? { ...t, lastMsg: msg.body, lastTs: msg.created_at, unread: t.unread + (selectedId === msg.client_id ? 0 : 1) }
                : t
              ).sort((a, b) => new Date(b.lastTs) - new Date(a.lastTs))
            }
            // New thread
            loadThreads()
            return prev
          })
          if (selectedId === msg.client_id) {
            setMessages(prev => [...prev, msg])
          }
        }
      })
      .subscribe()
    return () => { channelRef.current?.unsubscribe() }
  }, [selectedId])

  // Scroll to bottom when messages change
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Focus input when thread opens
  useEffect(() => {
    if (selectedId) setTimeout(() => inputRef.current?.focus(), 80)
  }, [selectedId])

  async function loadThreads() {
    const { data: msgs } = await supabase
      .from('messages')
      .select('id, client_id, body, sender, read, created_at, clients(name)')
      .order('created_at', { ascending: false })

    if (!msgs) return

    // Group by client
    const map = {}
    for (const m of msgs) {
      const cid = m.client_id
      if (!map[cid]) {
        map[cid] = {
          client_id: cid,
          name: m.clients?.name || 'Client',
          lastMsg: m.body || '',
          lastTs: m.created_at,
          unread: 0,
        }
      }
      if (m.sender === 'client' && !m.read) map[cid].unread++
    }

    const sorted = Object.values(map).sort((a, b) => new Date(b.lastTs) - new Date(a.lastTs))
    setThreads(sorted)
    setTotalUnread(sorted.reduce((s, t) => s + t.unread, 0))
  }

  async function openThread(clientId) {
    setSelectedId(clientId)
    const { data } = await supabase
      .from('messages')
      .select('*')
      .eq('client_id', clientId)
      .order('created_at', { ascending: true })
    setMessages(data || [])

    // Mark client messages as read
    await supabase.from('messages').update({ read: true }).eq('client_id', clientId).eq('sender', 'client').eq('read', false)
    setThreads(prev => prev.map(t => t.client_id === clientId ? { ...t, unread: 0 } : t))
    setTotalUnread(prev => Math.max(0, prev - (threads.find(t => t.client_id === clientId)?.unread || 0)))
  }

  async function send(e) {
    e.preventDefault()
    if (!input.trim() || !selectedId || sending) return
    setSending(true)
    const body = input.trim()
    setInput('')
    const { data } = await supabase.from('messages').insert({
      client_id: selectedId,
      body,
      sender: 'admin',
      read: false,
    }).select().single()
    if (data) {
      setMessages(prev => [...prev, data])
      setThreads(prev => prev.map(t => t.client_id === selectedId ? { ...t, lastMsg: body, lastTs: data.created_at } : t))
    }
    setSending(false)
  }

  const selectedThread = threads.find(t => t.client_id === selectedId)

  return (
    <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 900, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10 }}>
      {/* Chat panel */}
      {open && (
        <div style={{
          width: 380, height: 520, background: 'var(--card)', borderRadius: 16,
          border: '1px solid rgba(0,0,0,0.1)', boxShadow: '0 16px 48px rgba(0,0,0,0.16)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
          {/* Header */}
          <div style={{ background: 'var(--card)', borderBottom: '1px solid var(--bdr)', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            {selectedId ? (
              <>
                <button onClick={() => setSelectedId(null)} style={{ width: 28, height: 28, borderRadius: 7, background: 'var(--off)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--txt)', flexShrink: 0 }}>
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M7 2L3 5l4 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--surf)', border: '1px solid var(--bdr)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontFamily: 'var(--fd)', fontSize: 10, fontWeight: 700, color: 'var(--txt)' }}>
                  {initials(selectedThread?.name)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: 'var(--fd)', fontSize: 13, fontWeight: 700, color: 'var(--txt)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedThread?.name || 'Client'}</div>
                  <div style={{ fontFamily: 'var(--fu)', fontSize: 9, color: 'var(--mut)', marginTop: 1 }}>Client conversation</div>
                </div>
              </>
            ) : (
              <>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: 'var(--fd)', fontSize: 14, fontWeight: 700, color: 'var(--txt)' }}>Messages</div>
                  <div style={{ fontFamily: 'var(--fu)', fontSize: 10, color: 'var(--mut)', marginTop: 1 }}>{threads.length} conversation{threads.length !== 1 ? 's' : ''}</div>
                </div>
              </>
            )}
            <button onClick={() => setOpen(false)} style={{ width: 28, height: 28, borderRadius: 7, background: 'var(--off)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--txt)', flexShrink: 0 }}>
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 2l6 6M8 2l-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
            </button>
          </div>

          {/* Thread list */}
          {!selectedId && (
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {threads.length === 0 ? (
                <div style={{ padding: '48px 20px', textAlign: 'center' }}>
                  <div style={{ fontSize: 28, marginBottom: 10 }}>💬</div>
                  <div style={{ fontFamily: 'var(--fu)', fontSize: 12, color: 'var(--hint)' }}>No messages yet</div>
                </div>
              ) : threads.map((t, i) => (
                <button key={t.client_id} onClick={() => openThread(t.client_id)}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: 'none', border: 'none', borderBottom: i < threads.length - 1 ? '1px solid var(--bdr)' : 'none', cursor: 'pointer', textAlign: 'left' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--off)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--surf)', border: '1px solid var(--bdr)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--fd)', fontSize: 12, fontWeight: 700, color: 'var(--txt)' }}>
                      {initials(t.name)}
                    </div>
                    {t.unread > 0 && (
                      <div style={{ position: 'absolute', top: -2, right: -2, minWidth: 14, height: 14, borderRadius: 7, background: 'var(--red)', border: '1.5px solid #fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--fu)', fontSize: 8, fontWeight: 700, color: '#fff', padding: '0 3px' }}>
                        {t.unread}
                      </div>
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
                      <span style={{ fontFamily: 'var(--fu)', fontSize: 12, fontWeight: t.unread > 0 ? 700 : 500, color: 'var(--navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</span>
                      <span style={{ fontFamily: 'var(--fu)', fontSize: 10, color: 'var(--hint)', flexShrink: 0, marginLeft: 8 }}>{timeAgo(t.lastTs)}</span>
                    </div>
                    <div style={{ fontFamily: 'var(--fu)', fontSize: 11, color: t.unread > 0 ? 'var(--txt)' : 'var(--hint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: t.unread > 0 ? 500 : 400 }}>
                      {t.lastMsg?.slice(0, 55) || '…'}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Conversation thread */}
          {selectedId && (
            <>
              <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {messages.length === 0 && (
                  <div style={{ margin: 'auto', textAlign: 'center', color: 'var(--hint)', fontFamily: 'var(--fu)', fontSize: 12 }}>No messages yet — say hello!</div>
                )}
                {messages.map((msg, i) => {
                  const isAdmin = msg.sender === 'admin'
                  const showTime = i === 0 || new Date(msg.created_at) - new Date(messages[i-1]?.created_at) > 300000
                  return (
                    <div key={msg.id}>
                      {showTime && (
                        <div style={{ textAlign: 'center', fontFamily: 'var(--fu)', fontSize: 9, color: 'var(--hint)', margin: '4px 0 8px' }}>
                          {new Date(msg.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: isAdmin ? 'flex-end' : 'flex-start' }}>
                        <div style={{
                          maxWidth: '72%', padding: '8px 12px', borderRadius: isAdmin ? '12px 12px 3px 12px' : '12px 12px 12px 3px',
                          background: isAdmin ? 'var(--accent-soft)' : '#F1F5F9',
                          color: isAdmin ? 'var(--accent)' : 'var(--txt)',
                          fontFamily: 'var(--fu)', fontSize: 12, lineHeight: 1.45,
                        }}>
                          {msg.body}
                        </div>
                      </div>
                    </div>
                  )
                })}
                <div ref={bottomRef} />
              </div>

              {/* Input */}
              <form onSubmit={send} style={{ padding: '10px 14px', borderTop: '1px solid var(--bdr)', display: 'flex', gap: 8, background: 'var(--card)', flexShrink: 0 }}>
                <input
                  ref={inputRef}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  placeholder="Type a message…"
                  style={{ flex: 1, padding: '8px 12px', border: '1px solid var(--bdr2)', borderRadius: 8, fontFamily: 'var(--fu)', fontSize: 12, outline: 'none', background: 'var(--off)' }}
                  onFocus={e => e.target.style.borderColor = 'var(--navy)'}
                  onBlur={e => e.target.style.borderColor = 'var(--bdr2)'}
                />
                <button type="submit" disabled={!input.trim() || sending}
                  style={{ width: 36, height: 36, borderRadius: 9, background: input.trim() ? 'var(--accent-soft)' : 'var(--off)', border: 'none', cursor: input.trim() ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background 0.15s' }}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M12 7H2M12 7L8 3M12 7L8 11" stroke={input.trim() ? 'var(--accent)' : 'var(--hint)'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
              </form>
            </>
          )}
        </div>
      )}

      {/* Floating button */}
      <button
        onClick={() => { setOpen(o => !o); if (!open) loadThreads() }}
        style={{
          width: 52, height: 52, borderRadius: '50%', background: 'var(--card)',
          border: open ? '1px solid var(--bdr)' : '1px solid color-mix(in srgb, var(--accent) 40%, transparent)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.18)', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.2s', position: 'relative', flexShrink: 0,
        }}>
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
          <path d="M3 4h16a1 1 0 011 1v9a1 1 0 01-1 1H7L3 18.5V5a1 1 0 010-1z" stroke={open ? 'var(--navy)' : 'var(--accent)'} strokeWidth="1.5" strokeLinejoin="round"/>
        </svg>
        {!open && totalUnread > 0 && (
          <span style={{ position: 'absolute', top: 2, right: 2, minWidth: 18, height: 18, borderRadius: 9, background: 'var(--red)', border: '2px solid #fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--fu)', fontSize: 9, fontWeight: 700, color: '#fff', lineHeight: 1, padding: '0 4px' }}>
            {totalUnread > 9 ? '9+' : totalUnread}
          </span>
        )}
      </button>
    </div>
  )
}
